import { Injectable, InjectionToken, inject } from '@angular/core';
import { DiagConsoleLogger, DiagLogLevel, diag } from '@opentelemetry/api';
import { Logger, SeverityNumber } from '@opentelemetry/api-logs';
import { defaultResource, resourceFromAttributes } from '@opentelemetry/resources';
import {
    BatchLogRecordProcessor,
    LogRecordExporter,
    LogRecordProcessor,
    LoggerProvider,
    SimpleLogRecordProcessor
} from '@opentelemetry/sdk-logs';
import { BufferConfig } from '@opentelemetry/sdk-trace-base';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { filter } from 'rxjs';

import {
    ConfigurationService,
    Logger as RwcLogger,
    ShutdownService,
    UserSessionService
} from '@mobi/rwc-utils-common-ng-jslib';

import { TelemetryUserContext } from '../telemetry-user-context.service';

import { ConsoleLogRecordExporter } from './console-log-record-exporter';

export const LOG_RECORD_EXPORTER = new InjectionToken<LogRecordExporter>(
    'LOG_RECORD_EXPORTER token is required to export the logs to the correct endpoint.'
);

export const LOG_APPENDER_CONFIG_OPTIONS = new InjectionToken<LoggerConfigOptions>(
    'Log appender configuration options, like log severity.'
);

export interface LoggerConfigOptions {
    logSeverityTelemetry?: SeverityNumber;
    logSeverityConsole?: SeverityNumber;
    logDiagLevel?: DiagLogLevel;
}

@Injectable()
export class OtelLogAppenderService {
    #loggerConfig = inject<LoggerConfigOptions>(LOG_APPENDER_CONFIG_OPTIONS, { optional: true });
    /**
     * Can be set at runtime to change the severity level of logs that are sent to the backend.
     */
    public logSeverityTelemetry = this.#loggerConfig?.logSeverityTelemetry ?? SeverityNumber.WARN;

    /**
     * Can be set at runtime to change the severity level of logs that are printed to the console.
     */
    public logSeverityConsole = this.#loggerConfig?.logSeverityConsole ?? SeverityNumber.DEBUG;

    readonly #config: BufferConfig = {
        maxQueueSize: 200,
        maxExportBatchSize: 20,
        scheduledDelayMillis: 15_000,
        exportTimeoutMillis: 30_000
    };
    #configService = inject(ConfigurationService);
    #shutdownService = inject(ShutdownService);
    #userSessionService = inject(UserSessionService);
    #logRecordExporter = inject(LOG_RECORD_EXPORTER);
    #rwcLogger = inject(RwcLogger);
    #telemetryLogger?: Logger;
    #consoleLogger: Logger;
    #started = false;
    #userId = inject(TelemetryUserContext).getUserIds();

    constructor() {
        diag.setLogger(new DiagConsoleLogger(), { logLevel: this.#loggerConfig?.logDiagLevel ?? DiagLogLevel.NONE });
        const telemetryLogProcessor = new BatchLogRecordProcessor(this.#logRecordExporter, this.#config);
        const consoleLogProcessor = new SimpleLogRecordProcessor(new ConsoleLogRecordExporter());
        const isLocalDev = this.#configService.getSync('stage') === 'local-dev';
        if (!isLocalDev) {
            this.#telemetryLogger = this.#createProvider(telemetryLogProcessor).getLogger('RwcUtilsTelemetryLogger');
        } else {
            console.info('TELEMETRY: Logs disabled on local-dev environment');
        }
        this.#consoleLogger = this.#createProvider(consoleLogProcessor).getLogger('RwcUtilsConsoleLogger');
    }

    #createProvider(processor: LogRecordProcessor): LoggerProvider {
        const tkId = this.#configService.getTkNameId();
        const version = this.#configService.getSync('version');
        const sessionId = this.#userSessionService.getId();
        const resource = resourceFromAttributes({
            ...defaultResource().attributes,
            [ATTR_SERVICE_NAME]: tkId,
            [ATTR_SERVICE_VERSION]: version,
            'user.id': this.#userId,
            'user.session': sessionId
        });
        const provider = new LoggerProvider({ resource, processors: [processor] });
        this.#shutdownService.beforeShutdown$.subscribe(() => provider.forceFlush());
        return provider;
    }

    start(): void {
        if (this.#started) {
            diag.warn('OtelLogAppenderService already started.');
            return;
        }
        this.#started = true;

        const logsTelemetry$ = this.#rwcLogger.logs$.pipe(
            filter(log => log.severityNumber >= this.logSeverityTelemetry)
        );
        const logsConsole$ = this.#rwcLogger.logs$.pipe(filter(log => log.severityNumber >= this.logSeverityConsole));
        logsTelemetry$.subscribe(log => this.#telemetryLogger?.emit(log));
        logsConsole$.subscribe(log => this.#consoleLogger.emit(log));
    }
}
